import { appServiceApi } from '@/app/apis/app-service.api';
import { useAppDispatch, useAppSelector } from '@/app/app.store';
import { ReloadIcon } from '@/icons';
import { cn } from '@/lib/cn';
import { Button, Tooltip } from 'antd';

type Props = {
  /**
   * RTK Query endpoint names (e.g. `getAllOffices`, `getFailedOfficesJobs`) whose
   * lists should reload. Only the arguments already cached (the page and filters
   * on screen) are refetched, so nothing else on the page reloads.
   */
  endpoints: string[];
  tooltip?: string;
  className?: string;
};

const getEndpoint = (name: string) =>
  (appServiceApi.endpoints as Record<string, any>)[name];

/** Every cached argument set of the given endpoints, i.e. the lists on screen now. */
const getCachedQueries = (state: any, endpoints: string[]) =>
  endpoints.flatMap((name) =>
    getEndpoint(name)
      ? appServiceApi.util
          .selectCachedArgsForQuery(state, name as never)
          .map((arg) => ({ name, arg }))
      : [],
  );

/**
 * Reloads lists whose data is filled in the background (e.g. by a queued Excel
 * import), without reloading the page.
 */
export default function RefreshButton({
  endpoints,
  tooltip = 'Refresh',
  className,
}: Props) {
  const dispatch = useAppDispatch();

  const isRefreshing = useAppSelector((state) =>
    getCachedQueries(state, endpoints).some(
      ({ name, arg }) => getEndpoint(name).select(arg)(state)?.status === 'pending',
    ),
  );

  const handleRefresh = () => {
    dispatch((thunkDispatch, getState) => {
      getCachedQueries(getState(), endpoints).forEach(({ name, arg }) => {
        thunkDispatch(
          getEndpoint(name).initiate(arg, { subscribe: false, forceRefetch: true }),
        );
      });
    });
  };

  return (
    <Tooltip title={tooltip}>
      <Button
        size="large"
        aria-label={tooltip}
        disabled={isRefreshing}
        onClick={handleRefresh}
        className={cn(
          'btn-primary-bordered !h-10 !w-10 !p-0 shrink-0',
          className,
        )}
        icon={<ReloadIcon size={20} className={cn(isRefreshing && 'animate-spin')} />}
      />
    </Tooltip>
  );
}
